import { Typography } from '@mui/material';
import { checkSIIDocumentForUploadingInvoices } from 'lib/api/sii';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import Uploader from 'lib/designSystem/Uploader';
import { formatDateBack, showAlert } from 'lib/functions/utils';
import Papa from 'papaparse';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';

import { NewDocument } from '..';

interface AddDocumentsCSVProps {
  setDocuments: (documents: NewDocument[]) => void;
  stakeholderIdentifier: string;
}

interface ParsedDocument {
  Folio: string;
  'Razon Social Proveedor': string;
  'Rut Proveedor': string;
  'Fecha Emision': string;
  'Monto Total': string;
  Codigo: string;
}

interface ValidatedDocument extends NewDocument {
  valid: boolean;
  cessionable: boolean;
  message: string;
}

const AddDocumentsCSV = (props: AddDocumentsCSVProps) => {
  const { setDocuments, stakeholderIdentifier } = props;
  const dispatch = useDispatch();
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    setUploadedFileName(uploadedFile.name);

    let parsedFile: ParsedDocument[] = [];

    if (uploadedFile.name.endsWith('.csv')) {
      parsedFile = await parseCSV(uploadedFile);
    } else if (
      uploadedFile.name.endsWith('.xlsx') ||
      uploadedFile.name.endsWith('.xls')
    ) {
      parsedFile = await parseXLSX(uploadedFile);
    } else {
      showAlert(
        dispatch,
        'error',
        'Formato de archivo no soportado. Solo .csv y .xlsx',
      );
      return;
    }

    const formattedFile = formatedCSV(parsedFile);
    await validateDocuments(formattedFile);
  };

  const formatedCSV = (parsedFile: ParsedDocument[]) => {
    return parsedFile.map((document: ParsedDocument) => {
      const code = document['Codigo'] ? document['Codigo'] : '33';
      return {
        folio: document.Folio,
        businessIdentifier: document['Rut Proveedor'],
        businessName: document['Razon Social Proveedor'],
        stakeholderIdentifier: stakeholderIdentifier,
        issuedDate: document['Fecha Emision'],
        amount: Number(document['Monto Total']),
        subType: code,
      };
    });
  };

  const validateDocuments = async (documents: NewDocument[]) => {
    try {
      setLoading(true);
      const newDocuments = documents.map((document: NewDocument) => {
        const [yyyy, mm, dd] = document.issuedDate.split('/');
        const date = `${dd}${mm}${yyyy}`;
        return {
          ...document,
          issuedDate: date,
          type:
            document.subType === '33'
              ? 'electronic-invoice'
              : document.subType === '34'
                ? 'electronic-invoice-exempt'
                : 'electronic-invoice-buyer',
        };
      });
      const response = await checkSIIDocumentForUploadingInvoices(dispatch, {
        invoices: newDocuments,
      });
      if (response.length) {
        const failedInvoices = response.filter(
          (doc: ValidatedDocument) => !doc.valid,
        );

        const validDocumentsFolios = response
          .filter((doc: ValidatedDocument) => doc.valid)
          .map((doc: ValidatedDocument) => doc.folio);

        const validDocuments = newDocuments.filter((document: NewDocument) =>
          validDocumentsFolios.includes(document.folio),
        );

        if (failedInvoices.length) {
          setDocuments(
            validDocuments.map((document: NewDocument) => {
              return {
                ...document,
                issuedDate: formatDateBack(document.issuedDate),
              };
            }),
          );

          showAlert(
            dispatch,
            'error',
            `Los siguientes documentos no son válidos: ${failedInvoices
              .map((doc: ValidatedDocument) => doc.folio)
              .join(', ')}`,
          );
          return false;
        } else {
          setDocuments(
            validDocuments.map((document: NewDocument) => {
              return {
                ...document,
                issuedDate: formatDateBack(document.issuedDate),
              };
            }),
          );
          return true;
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const parseCSV = (file: File): Promise<ParsedDocument[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse<ParsedDocument>(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => resolve(result.data),
        error: (error) => reject(error),
      });
    });
  };

  const parseXLSX = (file: File): Promise<ParsedDocument[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(
          worksheet,
          {
            defval: '',
            raw: false, // permite que fechas se entreguen como strings si es posible
          },
        );

        const parsed = json.map((row): ParsedDocument => {
          const fechaRaw = String(row['Fecha Emision'] || '');

          const parseFecha = (value: string) => {
            const [dd, mm, yyyy] = value.split('/');
            let year = yyyy;
            if (year.length === 2) {
              year = `20${year}`;
            }
            const date = `${year}/${mm}/${dd}`;
            return date;
          };

          return {
            Folio: String(row.Folio || ''),
            'Razon Social Proveedor': String(
              row['Razon Social Proveedor'] || '',
            ),
            'Rut Proveedor': String(row['Rut Proveedor'] || ''),
            'Fecha Emision': parseFecha(fechaRaw),
            'Monto Total': String(
              Number(
                String(row['Monto Total'] || '')
                  .split(',')
                  .join(''),
              ),
            ),
            Codigo: String(row.Codigo || ''),
          };
        });

        resolve(parsed);
      };
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });
  };

  const downloadTemplate = () => {
    // build template data
    const template = [
      {
        Folio: '123',
        'Razon Social Proveedor': 'TECH SPA',
        'Rut Proveedor': '11111111-1',
        'Fecha Emision': '25/08/21',
        'Monto Total': '100000',
        Codigo: '33',
      },
    ];

    // build xlsx workbook
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(template);

    // build columns
    const columns = [
      [
        'Folio',
        'Razon Social Proveedor',
        'Rut Proveedor',
        'Fecha Emision',
        'Monto Total',
        'Codigo',
      ],
    ];
    XLSX.utils.sheet_add_aoa(worksheet, columns, { origin: 'A1' });

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'template.xlsx');
  };

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div style={{ padding: '10px 0px' }}>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <>
          <Typography variant="body1" style={{ paddingBottom: '10px' }}>
            Agrega el archivo xlsx con las facturas que quieres ingresar
          </Typography>
          <Uploader
            handleFileChange={handleFileChange}
            buttonText="Agregar XLSX"
            accept=".xlsx,.xls"
            uploadedFileName={uploadedFileName}
          />
          <Typography
            variant="body1"
            textAlign="center"
            style={{
              paddingTop: '10px',
              cursor: 'pointer',
              color: 'blue',
              fontWeight: 'bold',
            }}
            onClick={downloadTemplate}
          >
            Descargar template
          </Typography>
        </>
      )}
    </div>
  );
};

export default AddDocumentsCSV;
